/**
 * Class merging that knows this design system's tokens. `cn` and `tv` share one config, so a
 * variant and a caller's `className` resolve conflicts the same way.
 */
import { SEMANTIC_ROLES } from "@loot-os/api-contract";
import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
import { createTV } from "tailwind-variants";

import { RADIUS_TIERS, TYPE_ROLES } from "./presets.js";
import { RARITY_ROLES } from "./themeVars.js";

/** Every color token after `--color-`, as a utility names it (`bg-bg-surface` → `bg-surface`). */
export const COLOR_TOKENS = [
  ...SEMANTIC_ROLES.filter((key) => key !== "rarity-chase"),
  ...RARITY_ROLES.map((role) => `rarity-${role}`),
  "rarity-chase",
  "text-on-warning",
  "text-on-success",
  "chat-name-1",
  "chat-name-2",
  "chat-name-3",
  "chat-name-4",
  "chat-name-5",
  "chat-name-6",
  "chart-1",
  "chart-2",
  "chart-3",
  "chart-4",
  "chart-5",
  "volatility-0",
  "volatility-1",
  "volatility-2",
  "volatility-3",
  "volatility-4",
  "stage-1",
  "stage-2",
  "stage-3",
  "stage-4",
] as const;

/** The tailwind-merge extension for this design system's theme keys. */
export const mergeConfig = {
  extend: {
    theme: {
      color: [...COLOR_TOKENS],
      text: Object.keys(TYPE_ROLES),
      radius: [...Object.keys(RADIUS_TIERS), "pill", "control", "field"],
      shadow: ["float", "art", "glow-chase", "glow-brand"],
      ease: ["standard", "enter", "exit"],
      font: ["body", "display", "mono"],
      spacing: ["control-sm", "control-md", "control-lg", "control-xl"],
    },
  },
};

const twMerge = extendTailwindMerge(mergeConfig);

/** Joins classes, letting a later class win over an earlier one of the same kind. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** `tailwind-variants` with the same merge config as `cn`. */
export const tv = createTV({ twMergeConfig: mergeConfig });
